import { Post } from '__generated__/graphql/legacy/graphql';
import { eventNames } from 'constants/eventNames';
import { setGlobalCSSVariables } from 'helpers/styleHelpers';
import useEventPanel from 'hooks/Events/useEventPanel';
import Head from 'next/head';
import Link from 'next/link';
import PubSub from 'pubsub-js';

import React, { ComponentPropsWithRef, ReactNode, RefObject, useEffect, useState } from 'react';

import { ChalkCustomAnchorProvider } from '@thinkific/chalk/react-providers';

import { useCommunityContext } from 'components/CommunityContext';
import EventPanel from 'components/Events/components/EventPanel/EventPanel';
import ModeratorPanel from 'components/ModeratorPanel';
import ProfilePanel from 'components/Profile/ProfilePanel';
import { ReplyEditorProvider } from 'components/ReplyEditorContext/ReplyEditorContext';
import ReplyPanel from 'components/ReplyPanel/ReplyPanel';
import UserAvatarMenu from 'components/UserAvatarMenu/UserAvatarMenu';
import NewConversationModal from 'components/new/NewConversationModal/NewConversationModal';
import VariableOverrides from 'components/new/chat/Conversations/overrides/VariableOverrides';
import Layout from 'components/new/layout';
import CommunityThemeProvider from 'components/new/providers/CommunityThemeProvider';
import GlobalStateProvider from 'components/new/providers/GlobalStateProvider';

interface PubSubInfo {
  userId: string;
  lastElementRef: RefObject<HTMLElement | null>;
  post?: Post;
  commentIdFromUrl?: string;
}

function NextjsLinkWrapper({ href, ...props }: ComponentPropsWithRef<'a'>) {
  // needed due to Link `href` prop not accepting `undefined` (but `<a … />` replacement should be able to accept it)
  return <Link {...props} href={href ?? ''} />;
}

const SiteLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isProfilePanelOpen, setIsProfilePanelOpen] = useState<boolean>(false);
  const [isModeratorPanelOpen, setIsModeratorPanelOpen] = useState<boolean>(false);
  const [isUserAvatarMenuOpen, setIsUserAvatarMenuOpen] = useState<boolean>(false);
  const [isReplyPanelOpen, setIsReplyPanelOpen] = useState<boolean>(false);
  const [lastFocusedElementRef, setLastFocusedElementRef] =
    useState<RefObject<HTMLElement | null>>();
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedPost, setSelectedPost] = useState<Post | undefined>();
  const { community, site, currentUser } = useCommunityContext();
  const {
    eventId,
    handleCloseEventPanel,
    isOpen: isEventPanelOpen,
  } = useEventPanel(() => setIsProfilePanelOpen(false));

  useEffect(() => {
    setGlobalCSSVariables(community.primaryColor);
  }, [community]);

  useEffect(() => {
    const profileToken = PubSub.subscribe(
      eventNames.OPEN_PROFILE_PANEL,
      (msg: string, info: PubSubInfo) => {
        handleOpenProfilePanel(info);
      }
    );

    const sideMenuToken = PubSub.subscribe(
      eventNames.OPEN_SIDE_MENU,
      (msg: string, info: PubSubInfo) => {
        handleOpenUserAvatarMenu(info);
      }
    );

    const moderatorToken = PubSub.subscribe(
      eventNames.OPEN_MODERATOR_PANEL,
      (msg: string, info: PubSubInfo) => {
        handleOpenModeratorPanel(info);
      }
    );

    const replyToken = PubSub.subscribe(
      eventNames.OPEN_REPLY_PANEL,
      (msg: string, info: PubSubInfo) => {
        handleOpenReplyPanel(info);
      }
    );

    return () => {
      PubSub.unsubscribe(profileToken);
      PubSub.unsubscribe(sideMenuToken);
      PubSub.unsubscribe(moderatorToken);
      PubSub.unsubscribe(replyToken);
    };
  }, []);

  const handleCloseProfilePanel = () => {
    lastFocusedElementRef?.current?.focus();
    setSelectedUserId('');
    setIsProfilePanelOpen(false);
  };

  const handleOpenProfilePanel = (info: PubSubInfo) => {
    handleCloseEventPanel();
    setLastFocusedElementRef(info.lastElementRef);
    setSelectedUserId(info.userId);
    setIsProfilePanelOpen(true);
  };

  const handleOpenUserAvatarMenu = (info: PubSubInfo) => {
    handleCloseEventPanel();
    setLastFocusedElementRef(info.lastElementRef);
    setSelectedUserId(info.userId);
    setIsUserAvatarMenuOpen(true);
  };

  const handleCloseUserAvatarMenu = () => {
    lastFocusedElementRef?.current?.focus();
    setSelectedUserId('');
    setIsUserAvatarMenuOpen(false);
  };

  const handleOpenModeratorPanel = (info: PubSubInfo) => {
    setLastFocusedElementRef(info.lastElementRef);
    setIsModeratorPanelOpen(true);
  };

  const handleCloseModeratorPanel = () => {
    lastFocusedElementRef?.current?.focus();
    setIsModeratorPanelOpen(false);
  };

  const handleOpenReplyPanel = (info: PubSubInfo) => {
    document.body.style.overflow = 'hidden';
    setLastFocusedElementRef(info.lastElementRef);
    setSelectedPost(info?.post);
    setIsReplyPanelOpen(true);
  };

  const handleCloseReplyPanel = () => {
    document.body.style.overflow = 'visible';
    lastFocusedElementRef?.current?.focus();
    setIsReplyPanelOpen(false);
  };

  return (
    <>
      <Head>
        <title>{community.name}</title>
        <meta content={community.name} name="description" />
        <link href={site.favicon} rel="shortcut icon" type="image/x-icon" />
      </Head>
      <GlobalStateProvider>
        <CommunityThemeProvider>
          <ChalkCustomAnchorProvider value={NextjsLinkWrapper}>
            <div className="site-container">
              <Layout.SiteNav />
              <div className="app-content">
                <Layout.GlobalHeader />
                {children}
                <ProfilePanel
                  isOpen={isProfilePanelOpen}
                  userId={selectedUserId}
                  onClose={handleCloseProfilePanel}
                />
                <UserAvatarMenu
                  communityId={community.id}
                  isOpen={isUserAvatarMenuOpen}
                  user={currentUser}
                  onClose={handleCloseUserAvatarMenu}
                />
                <EventPanel
                  eventId={eventId}
                  isOpen={isEventPanelOpen}
                  onClose={handleCloseEventPanel}
                />
                <ModeratorPanel isOpen={isModeratorPanelOpen} onClose={handleCloseModeratorPanel} />
                <NewConversationModal />
                <ReplyEditorProvider>
                  <ReplyPanel
                    isOpen={isReplyPanelOpen}
                    postId={selectedPost?.id}
                    onClose={handleCloseReplyPanel}
                  />
                </ReplyEditorProvider>
                <Layout.MobileChin community={community} />
              </div>
            </div>
          </ChalkCustomAnchorProvider>
          <VariableOverrides />
        </CommunityThemeProvider>
      </GlobalStateProvider>
    </>
  );
};

export const getLayout = (page: ReactNode): ReactNode => <SiteLayout>{page}</SiteLayout>;

export default SiteLayout;
